<template>
  <container
    :isModal="true"
    @close-modal="closeScreen()"
    :loading="stateLoading"
  >
    <template v-slot:header>
      <h3>Importar Tocantins</h3>
    </template>
    <template v-slot:body>
      <form @submit.prevent="submitSave">
        <fieldset :disabled="stateLoading">
          <div class="p-formgrid p-grid">
            <div class="p-field p-col-12">
              <ul v-if="isIndividual">
                <li>
                  Insira o PDF da AET emitida pela AGETO. Se o PDF tiver mais de
                  uma AET, será importada apenas a desta licença.
                </li>
              </ul>
              <ul v-else>
                <li>
                  Insira o PDF emitido pela AGETO. Ele pode conter várias AETs;
                  cada uma será separada e salva na licença Tocantins
                  correspondente.
                </li>
                <li>
                  A licença é identificada pela placa do cavalo e pelo CPF/CNPJ
                  do transportador.
                </li>
                <li>
                  O número da AET e as datas de validade são lidos do próprio
                  PDF.
                </li>
                <li>
                  As carretas do conjunto principal da licença precisam constar
                  na AET. As de rodízio que não constarem são apenas avisadas.
                </li>
              </ul>
            </div>
            <div class="p-field p-col-12">
              <input-file
                label="PDF da AET Tocantins"
                :id="stateId + '_importacao'"
                accept="application/pdf"
                :hasDownload="false"
              />
            </div>
            <div class="p-col-12">
              <Button type="submit" class="p-button-rounded" label="Importar" />
            </div>
          </div>
        </fieldset>
      </form>
      <div v-if="importReturn">
        <div v-if="importReturn.sucesso && importReturn.sucesso.length">
          <div class="sub-title">
            <h4>AETS IMPORTADAS COM SUCESSO</h4>
          </div>
          <ul>
            <li v-for="aet in importReturn.sucesso" :key="aet">
              AET <b>{{ aet }}</b> importada.
            </li>
          </ul>
        </div>
        <div v-if="importReturn.erros && importReturn.erros.length">
          <div class="sub-title">
            <h4>AETS NÃO IMPORTADAS</h4>
          </div>
          <ul>
            <li v-for="erro in importReturn.erros" :key="erro.nome">
              <b>{{ erro.nome }}</b> : {{ erro.erro }}
            </li>
          </ul>
        </div>
      </div>
    </template>
  </container>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, computed, Ref } from "vue";
import { useLicencaOrgaoService } from "../../../modules/business/licenca/licencaOrgaoBusiness";
import { ImportacaoDocumentoReturn } from "../../../modules/business/veiculo/importacaoVeiculoBusiness";
import { useToast } from "../../../modules/message";

interface ImportacaoAetAgetoState {
  importReturn: ImportacaoDocumentoReturn | null;
  imported: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    // preenchidos: importa so a AET do registro Tocantins informado
    idLicenca: Number,
    idLicencaOrgao: Number,
    parentLoading: Boolean
  },
  setup(props, { emit }) {
    const state = reactive<ImportacaoAetAgetoState>({
      importReturn: null,
      imported: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const isIndividual = computed(
      () => !!(props.idLicenca && props.idLicencaOrgao)
    );

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || props.parentLoading;
    });

    const closeScreen = () => {
      emit("after-import", state.imported);
    };

    const clearFile = () => {
      const file = document.querySelector(
        "#" + state.stateId + "_importacao"
      ) as HTMLInputElement;
      if (file) {
        file.value = "";
      }
    };

    const submitSave = () => {
      state.importReturn = null;
      const file = document.querySelector(
        "#" + state.stateId + "_importacao"
      ) as HTMLInputElement;
      if (!file || !file.files || file.files.length === 0) {
        useToast().warning("É necessário um arquivo.");
        return;
      }
      // enviado direto ao endpoint: o PDF da AGETO passa do limite de 4MB do /storage/temp
      const formData = new FormData();
      formData.append("file", file.files[0]);

      const {
        importAetAgeto,
        importAetAgetoLote,
        loading,
        data
      } = useLicencaOrgaoService();
      state.loadings.push(loading);
      (isIndividual.value
        ? importAetAgeto(
            props.idLicenca as number,
            props.idLicencaOrgao as number,
            formData
          )
        : importAetAgetoLote(formData)
      )
        .then(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          clearFile();
          // lote e individual devolvem o mesmo retorno; o individual pode trazer o aviso das
          // carretas de rodizio que nao constam na AET, entao o modal fica aberto com o resultado
          state.importReturn = data.value;
          if (!state.importReturn) {
            useToast().warning("Não foi possível realizar a importação.");
          } else {
            state.imported = !!state.importReturn.sucesso?.length;
            if (state.importReturn.erros && state.importReturn.erros.length) {
              useToast().warning(
                "Arquivo processado com erros. Verifique o retorno."
              );
            } else {
              useToast().success("Importação realizada com sucesso.");
            }
          }
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    return {
      isIndividual,
      stateLoading,
      closeScreen,
      submitSave,
      ...toRefs(state)
    };
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss" />
